import { ToolContainer, FileDropZone, DownloadButton, downloadBlob, ResultPreview } from "@/components/ToolLayout";
import { useState, useCallback, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useFreemium } from "@/hooks/useFreemium";
import { CROSSFADE_SECONDS, type MergeShape } from "./mergePlan";
import { mismatchNote, mergeResultNote } from "./mergeNotes";
import { loadFFmpeg, checkFileSize, ffmpegOutputToBlob, stopProcessing, resetFFmpegEngine, mergeVideos } from "./operations";
import { looksLikeVideo } from "./utils";
import { VideoProcessingProgress, showVideoError, FileSizeNotice } from "./uiHelpers";
import { X, ArrowUp, ArrowDown } from "lucide-react";
import type { FFmpeg } from "@ffmpeg/ffmpeg";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";

const TOOL_INFO_CONTENT = {
  what: `Combines two or more video clips into a single video file, played back to back in the order you add them. Runs entirely in your browser — your videos never leave your device.`,
  how: `Upload your video clips in the order you want them to play, then download the combined video. Use the arrow buttons next to a clip to change the order. Each clip shows its length and size, so you can spot the odd one out.`,
  tip: `When all clips already match they are joined without re-encoding (fast, no quality loss). Otherwise they are re-encoded to one size and frame rate, with black bars (or cropped edges if you tick "Fill the frame") instead of stretching — choose the output shape above. Tick "Fade between clips" for a short crossfade at each join instead of a hard cut. Clips of very different quality may still look uneven where they join. The size limit applies to all clips added together, not each one separately. Some phone-recorded videos (particularly .mov files) use a codec this tool can't read — if processing fails right away, try re-exporting the video from your phone's own share options first.`,
};

interface ClipInfo { seconds: number; width: number; height: number }
const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;

/** Length and picture size of each clip, read from the browser's own player (no ffmpeg needed). */
function useClipInfo(files: File[]): Map<File, ClipInfo | null> {
  const [info, setInfo] = useState<Map<File, ClipInfo | null>>(new Map());
  useEffect(() => {
    let live = true;
    for (const f of files) {
      if (info.has(f)) continue;
      const url = URL.createObjectURL(f);
      const v = document.createElement("video");
      const done = (value: ClipInfo | null) => { URL.revokeObjectURL(url); if (live) setInfo((m) => new Map(m).set(f, value)); };
      v.preload = "metadata";
      v.onloadedmetadata = () => done(v.videoWidth ? { seconds: v.duration, width: v.videoWidth, height: v.videoHeight } : null);
      v.onerror = () => done(null);
      v.src = url;
    }
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);
  return info;
}

export default function MergeVideo() {
  const [files, setFiles] = useState<File[]>([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [result, setResult] = useState<Blob | null>(null);
  const [shape, setShape] = useState<MergeShape>("first");
  const [fill, setFill] = useState(false);
  const [crossfade, setCrossfade] = useState(false);
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const { logUsageNow } = useLogUsageOnAction();
  const clipInfo = useClipInfo(files);
  const totalSeconds = files.reduce((n, f) => n + (clipInfo.get(f)?.seconds ?? 0), 0);

  const handleFiles = useCallback((all: File[]) => {
    const newFiles = all.filter((f) => {
      if (looksLikeVideo(f)) return true;
      toast.error(`"${f.name}" isn't a video file`);
      return false;
    });
    setFiles(prev => {
      const combined = [...prev];
      let duplicatesSkipped = 0;
      for (const f of newFiles) {
        // Same name + size + modified time is as close to "the same file"
        // as the browser lets us check without reading full contents --
        // good enough to catch someone accidentally adding a clip twice.
        const isDuplicate = combined.some(
          existing => existing.name === f.name && existing.size === f.size && existing.lastModified === f.lastModified
        );
        if (isDuplicate) duplicatesSkipped++;
        else combined.push(f);
      }
      if (duplicatesSkipped > 0) {
        toast.error(
          duplicatesSkipped === 1
            ? "That file is already in the list — skipped the duplicate."
            : `${duplicatesSkipped} of those files are already in the list — skipped the duplicates.`
        );
      }
      return combined;
    });
    setResult(null);
  }, []);

  const removeFile = (i: number) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const moveFile = (i: number, direction: -1 | 1) => {
    setFiles(prev => {
      const target = i + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[target]] = [next[target], next[i]];
      return next;
    });
    setResult(null);
  };

  const sizeNote = mismatchNote(files.map((f) => clipInfo.get(f) ?? null), shape, fill);
  const sizeCheck = files.length > 0 ? checkFileSize(files, isPro || isLoading, isAdmin) : null;

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) stopProcessing(ffmpegRef.current);
  };

  const merge = async () => {
    if (files.length < 2) { toast.error("Add at least 2 videos to merge"); return; }
    // Logged here -- the actual merge action -- rather than on each
    // file drop, matching the same fix already applied to Merge PDF
    // and Audio Merger: adding clips across separate drops, or
    // removing a duplicate and re-adding one, no longer burns extra
    // free uses for what's still just one unfinished merge job.
    const allowed = await logUsageNow();
    if (!allowed) return;
    setProcessing(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading video engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Reading video files...");

      setStatusMessage("Merging clips...");
      const out = await mergeVideos(ffmpeg, files, setProgress, controller.signal, setStatusMessage, shape, fill, crossfade);

      setResult(ffmpegOutputToBlob(out.data, "video/mp4"));
      const note = mergeResultNote(out, fill);
      if (out.noSound) toast.success(`Videos merged${note}. None of the clips has sound.`);
      else if (out.silentClips > 0) toast.success(`Videos merged${note}. ${out.silentClips} clip(s) had no sound, so those parts are silent.`);
      else toast.success(`Videos merged${note}.`);
    } catch (err) {
      showVideoError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  return (
    <ToolContainer>
      <FileDropZone accept="video/*" multiple maxSize={5000} onFiles={handleFiles} label="Drop videos to merge" description="Combine multiple video clips" deferUsageLog />
      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          {files.map((f, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg border text-sm">
              <span>{i + 1}. {f.name} <span className="text-muted-foreground text-xs">({(f.size/1024/1024).toFixed(1)} MB{clipInfo.get(f) ? ` · ${fmtTime(clipInfo.get(f)!.seconds)} · ${clipInfo.get(f)!.width}×${clipInfo.get(f)!.height}` : ""})</span></span>
              <div className="flex items-center gap-0.5">
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveFile(i, -1)} disabled={processing || i === 0} title="Move earlier" aria-label={`Move ${f.name} earlier`}><ArrowUp className="h-3 w-3" /></Button>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveFile(i, 1)} disabled={processing || i === files.length - 1} title="Move later" aria-label={`Move ${f.name} later`}><ArrowDown className="h-3 w-3" /></Button>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeFile(i)} disabled={processing} title="Remove" aria-label={`Remove ${f.name}`}><X className="h-3 w-3" /></Button>
              </div>
            </div>
          ))}
          {totalSeconds > 0 && <p className="text-xs text-muted-foreground">Total length about {fmtTime(crossfade && files.length > 1 ? Math.max(0, totalSeconds - (files.length - 1) * CROSSFADE_SECONDS) : totalSeconds)}{crossfade && files.length > 1 ? ` (${CROSSFADE_SECONDS} s overlap at each join)` : ""}</p>}
          {sizeNote && <p role="status" className="text-xs text-muted-foreground" data-testid="merge-mismatch-note">{sizeNote}</p>}
          {files.length > 1 && (
            <label className="flex items-center gap-2 text-sm">Output shape
              <select aria-label="Output shape" className="border rounded px-2 py-1 bg-background" value={shape} onChange={(e) => { setShape(e.target.value as MergeShape); setResult(null); }} disabled={processing}>
                <option value="first">Same as the first clip</option>
                <option value="landscape">Landscape 1920 x 1080</option>
                <option value="portrait">Portrait 1080 x 1920 (Shorts / Reels)</option>
                <option value="square">Square 1080 x 1080</option>
              </select>
            </label>
          )}
          {files.length > 1 && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" aria-label="Fill the frame" checked={fill} onChange={(e) => { setFill(e.target.checked); setResult(null); }} disabled={processing} /> Fill the frame (crop the edges instead of adding black bars)</label>
          )}
          {files.length > 1 && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" aria-label="Fade between clips" checked={crossfade} onChange={(e) => { setCrossfade(e.target.checked); setResult(null); }} disabled={processing} /> Fade between clips ({CROSSFADE_SECONDS} s crossfade; the result is {CROSSFADE_SECONDS} s shorter per join)</label>
          )}
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}
          {processing && <div className="pt-2"><VideoProcessingProgress progress={progress} statusMessage={statusMessage} onStop={stop} /></div>}
          {result && <ResultPreview blob={result} />}
          <div className="flex gap-3 pt-4">
            {result ? (
              <>
                <DownloadButton blob={result} fileName={"merged.mp4"} />
                <Button variant="outline" onClick={() => { setFiles([]); setResult(null); }}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={merge} disabled={processing || files.length < 2 || sizeCheck?.severity !== "ok"}>{processing ? "Merging..." : "Merge Videos"}</Button>
                <Button variant="outline" onClick={() => setFiles([])} disabled={processing}>Clear</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
